import { Controller } from "@hotwired/stimulus"

// Seconds after sending at which the waiting reply moves to its next stage (reading the
// question, checking the rules, writing, taking long): see courses/ai_chats/_pending.
const STAGE_AFTER = [0, 3, 7, 15]

// The Ask AI chat panel (courses/ai_chats/_panel). Replies stream in over Turbo
// Streams; while one is pending (its placeholder carries data-ai-chat-pending)
// sending is disabled, and the list follows the newest turn.
//
// A broadcast can go missing (a dropped socket; in development the job worker's
// broadcasts never reach the web process). When a pending reply has shown no
// change for refreshAfter ms, the panel reloads its frame from the server, which
// renders the finished reply, or the placeholder again while it is still written.
export default class extends Controller {
    static targets = ["messages", "form", "input", "send", "chip", "stage"]
    static values = { refreshAfter: { type: Number, default: 12000 } }

    connect() {
        this.following = true
        this.scrollToLatest()
        if (!this.hasMessagesTarget) return

        this.observer = new MutationObserver(() => {
            this.scrollToLatest()
            this.syncPending()
        })
        this.observer.observe(this.messagesTarget, { childList: true, subtree: true, characterData: true })
        this.syncPending()
        // Desktop only: on a phone the keyboard would cover the suggestions.
        if (this.hasInputTarget && window.matchMedia("(min-width: 640px) and (pointer: fine)").matches) {
            this.inputTarget.focus({ preventScroll: true })
        }
    }

    disconnect() {
        this.observer?.disconnect()
        clearTimeout(this.refreshTimer)
        clearInterval(this.stageTimer)
    }

    // The composer is replaced after every send, so the pending state is re-read
    // whenever a new send button appears.
    sendTargetConnected() {
        this.syncPending()
    }

    submitOnEnter(event) {
        if (event.key !== "Enter" || event.shiftKey || event.isComposing) return

        event.preventDefault()
        if (this.inputTarget.value.trim() !== "" && !this.pending) this.formTarget.requestSubmit()
    }

    resize() {
        const input = this.inputTarget
        input.style.height = "auto"
        input.style.height = `${Math.min(input.scrollHeight, 140)}px`
        this.syncSend()
    }

    // The empty chat's suggestions, Continue under a cut-short reply, and Try again
    // under a failed one (which sends no chip).
    sendChip({ params: { chip, text } }) {
        if (!this.hasFormTarget || this.pending) return

        this.inputTarget.value = text
        if (this.hasChipTarget) this.chipTarget.value = chip || ""
        this.formTarget.requestSubmit()
    }

    submitStart() {
        this.following = true
        if (this.hasSendTarget) this.sendTarget.disabled = true
    }

    submitEnd(event) {
        if (!event.detail.success) this.syncSend()
    }

    // Law links open beside the test on the web; the Android app pushes them as a
    // new screen (data-turbo-frame="_top"), so they are left to Turbo there.
    openLink(event) {
        if (navigator.userAgent.includes("Hotwire Native")) return

        event.preventDefault()
        window.open(event.currentTarget.href, "_blank", "noopener")
    }

    // Runs on every change to the message list, so each streamed piece restarts
    // the wait before refresh.
    syncPending() {
        this.pending = this.hasMessagesTarget && this.messagesTarget.querySelector("[data-ai-chat-pending]") !== null
        this.syncSend()

        clearTimeout(this.refreshTimer)
        if (this.pending) this.refreshTimer = setTimeout(() => this.refresh(), this.refreshAfterValue)
        this.syncStage()
    }

    syncSend() {
        if (!this.hasSendTarget) return

        this.sendTarget.disabled = this.pending || (this.hasInputTarget && this.inputTarget.value.trim() === "")
    }

    // Walks the waiting reply through its stages until the first words replace them.
    syncStage() {
        if (!this.hasStageTarget) {
            clearInterval(this.stageTimer)
            this.stageTimer = null
            return
        }
        if (this.stageTimer) return

        const pending = this.stageTarget.closest("[data-ai-chat-pending]")
        this.stageStart = Date.now() - Number(pending?.dataset.aiChatElapsed || 0) * 1000
        this.showStage()
        this.stageTimer = setInterval(() => this.showStage(), 500)
    }

    showStage() {
        if (!this.hasStageTarget) return this.syncStage()

        const stages = JSON.parse(this.stageTarget.dataset.stages)
        const seconds = (Date.now() - this.stageStart) / 1000
        const index = STAGE_AFTER.filter((after) => seconds >= after).length - 1
        const text = stages[Math.min(index, stages.length - 1)]
        if (this.stageTarget.textContent !== text) this.stageTarget.textContent = text
    }

    refresh() {
        if (this.pending) this.element.closest("turbo-frame")?.reload()
    }

    // A scroll the student made: follow new turns again only once back at the bottom.
    trackScroll() {
        if (this.autoScrolling) {
            this.autoScrolling = false
            return
        }
        const list = this.messagesTarget
        this.following = list.scrollHeight - list.scrollTop - list.clientHeight < 64
    }

    // Brings the newest turn into view, but never past the student's latest message:
    // a long reply then reads from its start rather than from its end. Never scrolls up.
    scrollToLatest() {
        if (!this.hasMessagesTarget || !this.following) return

        const list = this.messagesTarget
        const question = [...list.querySelectorAll('[data-ai-chat-turn="user"]')].pop()
        const bottom = list.scrollHeight - list.clientHeight
        const target = question ? Math.min(bottom, question.offsetTop - 16) : bottom
        if (target <= list.scrollTop + 1) return

        this.autoScrolling = true
        list.scrollTop = target
    }
}
